import { PageTitle } from "@/components/page"
import { useDatabaseData, usePage } from "@/components/page/hooks"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Combobox } from "@/components/ui/combobox"
import { ChatMessage, WhatsAppChat } from "@/components/whatsapp-chat/whatsappChat"
import { Plus, RefreshCcw, Trash2 } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { supabase } from "@/lib/data"
import { ulid } from "ulid"
import { ContactEvaluationInfo } from "@/components/whatsapp-chat/contactevaluationInfo"
import { CRUDLoader } from "@/components/page/crudloader"
import { delay } from "@/lib/utils"
import { app } from "@/lib/app"
import { loadAgent } from "@/lib/agents/agentfactory"
import { toast } from "sonner"

const simluatedAgent: Contact = {
  id: ulid(),
  type: "ai",
  name: "[Simulated]",
}

export const agentChat = {
  title: "Agents Chat",
  description: "Chat with Agents.",
  route: "/chat",
  url: "/chat",
  component: component,
  group: "evaluations",
  resource: "agentchat",
  action: "read",
} satisfies PageConfig


export function component() {

  const storedOperator = localStorage.getItem("selectedContact")

  const { navigate, team, } = usePage()
  const agent = useRef<Agent>(null)
  const { data: eval_items, loading: eval_items_loading } = useDatabaseData("eval_items")
  const [selectedContactId, setSelectedContactId] = useState(null)
  const [operators, setOperators] = useState<Contact[]>([])
  const [selectedOperator, setSelectedOperator] = useState<any>(storedOperator)
  const [loadingAgents, setLoadingAgents] = useState(true)
  const [agents, setAgents] = useState<Contact[]>([])
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [messagesLoading, setMessagesLoading] = useState(false)

  async function loadContacts() {

    setMessages([])
    setSelectedContactId(null)
    setLoadingAgents(true)

    agent.current = null

    let { data: dbOperators } = await supabase.from("contacts").select().eq("team", team?.id).eq("type", "operator").order("created_at", { ascending: true })
    let { data: dbAgents } = await supabase.from("agents").select().eq("team_id", team?.id)

    dbOperators ||= []
    dbAgents ||= []

    const finalContacts: Contact[] = []

    for (const a of dbAgents) {
      const contact: Contact = {
        id: `${a.id}`,
        type: "ai",
        name: a.title,
        team: team?.id,
        // avatar: "",
      }
      finalContacts.push(contact)
    }

    setAgents(finalContacts)
    setOperators((dbOperators as any[]).map((c: Contact) => {
      c.type = "operator"
      return c as any
    }))
    setLoadingAgents(false)

  }

  useEffect(() => {
    if (team) {
      loadContacts().then(() => setLoadingAgents(false))
    }
  }, [team])


  async function onRefreshContacts() {
    await loadContacts()
  }

  async function loadMessages(agent: any, contact: any) {

    const { data, error } = await supabase.from("messages").select(`*`).eq("agent", agent).eq("contact", contact).order("created_at", { ascending: true })

    console.log("Messages: ", data)

    const newMessages: ChatMessage[] = []

    if (data && data.length > 0) {

      for (const md of data) {
        const m: ChatMessage = { ...md } as any

        if (m.role == "user") {
          m.contactObject = operators.find(c => c.id == m.contact as any)
          m.direction = "right"
        } else {
          m.contactObject = agents.find(c => c.id == m.agent as any)
          m.direction = "left"
        }
        newMessages.push(m)
      }
    }

    return newMessages
  }

  async function onContactSelected(id: number = null) {

    if (!id) {
      agent.current = null
      setSelectedContactId(null)
      setMessagesLoading(false)
      setMessages([])
      return
    }
    setMessagesLoading(true)
    agent.current = null
    const newMessages = await loadMessages(id, selectedOperator)
    setMessages(newMessages)
    setMessagesLoading(false)
    setSelectedContactId(id)
  }

  function onSelectedOperatorChange(v: string) {
    setSelectedContactId(null)
    localStorage.setItem("selectedContact", v)
    setMessages([])
    setSelectedOperator(v)
  }

  async function onSendMessage(agentID: any, message: string) {

    if (agent.current && agent.current.id != agentID) {
      console.log("Different Agent, reset")
      agent.current = null
    }

    if (!agent.current) {
      console.log(`Creating Agent for ${agentID}`)
      agent.current = await loadAgent(agentID, team.id)
    }

    const apiKeys = await app.fetchAPIkeys(team.id)


    const p: AgentParameters = {
      debug: true,
      apiKeys,
      input: {
        message,
      },
      integration: {
        contact: selectedOperator,
        type: "app",
      },
    }

    await agent.current.execute(p)

    const { data } = await supabase.from("contacts").select().eq("id", selectedOperator).single()
    setOperators(operators.map(c => c.id == selectedOperator ? data as any : c))

    const newMessages = await loadMessages(agentID, selectedOperator)
    setMessages(newMessages)

  }

  if (eval_items_loading) return <CRUDLoader />

  let selecteOperatorContact = operators.find(c => c.id == selectedOperator)

  const items: Record<string, Evaluation_Item> = {}
  for (const item of eval_items) {
    items[item.id] = item as any
  }



  return <>
    <div className="flex flex-col h-full min-h-0 overflow-hidden p-4 gap-4">
      <div className="flex shrink-0">
        <PageTitle />
        <div className="grow"></div>
        <div className="flex items-center gap-4">
          <div title="Refresh">
            <RefreshCcw className="h-8 w-8 text-muted-foreground cursor-pointer" onClick={onRefreshContacts} />
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0">
        <WhatsAppChat
          className="h-full"
          contacts={agents}
          messages={messages}
          selectedContactId={selectedContactId}
          onContactSelected={onContactSelected}
          loadingContacts={loadingAgents}
          loadingMessages={messagesLoading}
          onSendMessage={onSendMessage}
          hideContactName
        >
          <WhatsAppChat.Toolbar>
            <div className="flex w-full items-center mb-2">
              <div className="">{agents.length} Contacts</div>
              <div className="grow"></div>
              <div className="flex items-center gap-2">
                <div className="text-muted-foreground">Operator:</div>
                <Combobox
                  className="min-w-60"
                  items={operators.map(o => ({ value: o.id, label: o.name }))}
                  value={selectedOperator}
                  onChange={onSelectedOperatorChange}
                  placeholder="Search operator..."
                />
              </div>
            </div>
          </WhatsAppChat.Toolbar>

          <WhatsAppChat.ContactInfo>
            <ContactEvaluationInfo contact={selecteOperatorContact} items={items} />
          </WhatsAppChat.ContactInfo>

          {/* <WhatsAppChat.MessageInput>
            <div />
          </WhatsAppChat.MessageInput> */}

          {!selectedOperator && <WhatsAppChat.ChatPanel>
            <div className="grid place-items-center h-full bg-muted">
              Please select an operator.
            </div>
          </WhatsAppChat.ChatPanel>}

        </WhatsAppChat>
      </div>
    </div>
  </>
}
